import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  MapPin,
  NavigationArrow,
  Phone,
  Drop,
  ChatCircle,
  EnvelopeSimple,
  Printer,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtTime12 } from "@/lib/scheduleWindows";
import { ConfirmTextDialog } from "@/components/ConfirmTextDialog";
import LabelDialog from "@/components/LabelDialog";
import { JobEmailDialog } from "@/components/JobEmailDialog";

const NEXT = { Pending: "On site", "On site": "Done", Done: "Pending" };
const STATUS_STYLE = {
  Pending: "border-zinc-700 bg-zinc-950 text-zinc-300",
  "On site": "border-yellow-500 bg-yellow-500/15 text-yellow-300",
  Done: "border-emerald-500 bg-emerald-500/15 text-emerald-300",
};
const KIND_STYLE = {
  Template: "border-purple-500/30 text-purple-300",
  Fabrication: "border-yellow-500/30 text-yellow-300",
  Install: "border-blue-500/30 text-blue-300",
  Service: "border-red-500/30 text-red-300",
};
const ORDER = { Template: 0, Fabrication: 1, Install: 2, Service: 3 };

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtClock = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};
const addDaysIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const JobFieldView = () => {
  const { user } = useAuth();
  const [stops, setStops] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);
  const [texting, setTexting] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [labelDlg, setLabelDlg] = useState(null);
  const [emailJob, setEmailJob] = useState(null);
  const me = user?.name || user?.email;

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs/schedule");
    setStops(
      (data.events || []).sort(
        (a, b) =>
          a.date.localeCompare(b.date) ||
          (ORDER[a.kind] ?? 9) - (ORDER[b.kind] ?? 9),
      ),
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user?.role === "installer") setMineOnly(true);
  }, [user?.role]);

  const cycle = async (stop) => {
    const current = stop.stop_status?.status || "Pending";
    const next = NEXT[current];
    const url =
      stop.entity === "service"
        ? `/service-calls/${stop.job_id}/status`
        : `/jobs/${stop.job_id}/stops/${stop.kind}`;
    setStops((list) =>
      list.map((s) =>
        s.job_id === stop.job_id && s.kind === stop.kind
          ? { ...s, stop_status: { ...s.stop_status, status: next } }
          : s,
      ),
    );
    try {
      await api.patch(url, { status: next });
      toast.success(`${stop.code} ${stop.kind} · ${next}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not update",
      );
      load();
    }
  };

  const omwMessage = (s) =>
    `Hi${
      s.customer_name ? " " + s.customer_name.split(" ")[0] : ""
    }, this is Brioni Granite — our crew is on the way now for your ${s.kind.toLowerCase()}${
      s.window && s.window !== "All day" ? ` (arrival window ${s.window})` : ""
    }. See you shortly!`;

  const openOnMyWay = (s) => {
    if (!s.customer_phone) return;
    setConfirm({ s, to: s.customer_phone, message: omwMessage(s) });
  };

  const doSendOnMyWay = async (message) => {
    const s = confirm?.s;
    if (!s) return;
    setTexting(`${s.job_id}-${s.kind}`);
    try {
      await api.post("/sms/send", {
        to: s.customer_phone,
        body: message,
        entity: s.entity === "service" ? "service" : "job",
        entity_id: s.job_id,
      });
      toast.success("On-the-way text sent");
      setConfirm(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send text",
      );
    } finally {
      setTexting("");
    }
  };

  const visible = mineOnly ? stops.filter((s) => s.crew && s.crew === me) : stops;
  const today = todayIso();
  const weekEnd = addDaysIso(7);
  const isDone = (s) => (s.stop_status?.status || "Pending") === "Done";
  const groups = [
    ["Overdue", visible.filter((s) => s.date < today && !isDone(s))],
    ["Today", visible.filter((s) => s.date === today)],
    ["This week", visible.filter((s) => s.date > today && s.date <= weekEnd)],
    ["Later", visible.filter((s) => s.date > weekEnd)],
  ];
  const shown = groups.reduce((n, [, list]) => n + list.length, 0);

  const printDaySheet = (dateIso, label) => {
    const dayLabel = new Date(`${dateIso}T12:00:00`).toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
    const list = visible
      .filter((s) => s.date === dateIso)
      .sort(
        (a, b) =>
          (a.time || "99:99").localeCompare(b.time || "99:99") ||
          (ORDER[a.kind] ?? 9) - (ORDER[b.kind] ?? 9),
      );
    const esc = (v) => String(v || "").replace(/</g, "&lt;");
    const rows = list
      .map((s) => {
        const t = s.time ? fmtTime12(s.time) : "";
        const win = s.window && s.window !== "All day" ? s.window : "";
        const when = [t, win].filter(Boolean).join(" · ") || "All day";
        const sink = s.sink_location ? `Sink: ${esc(s.sink_location)}` : "";
        const ok = s.confirmed ? '<span style="color:#059669;font-weight:700">\u2713 </span>' : "";
        return `<tr><td class="t">${when}</td><td>${esc(s.kind)}</td><td>${ok}${esc(s.customer_name || s.project_name || s.code)}<div class="mono">${esc(s.code)}</div></td><td>${esc(s.address)}</td><td>${esc(s.crew || "Unassigned")}</td><td>${sink}</td></tr>`;
      })
      .join("");
    const bodyHtml = list.length
      ? `<table><thead><tr><th>Time / Window</th><th>Stage</th><th>Customer</th><th>Address</th><th>Crew</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table>`
      : `<div class="empty">No stops scheduled for this day.</div>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Day sheet — ${label}</title><style>body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#111;margin:24px}h1{font-size:20px;margin:0}.sub{color:#666;font-size:13px;margin:2px 0 16px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{text-align:left;padding:8px 10px;border-bottom:1px solid #ddd;vertical-align:top}th{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#666}td.t{white-space:nowrap;font-weight:700}.mono{color:#888;font-size:11px}.empty{color:#888;padding:24px 0}@media print{.noprint{display:none}}</style></head><body><h1>Brioni Granite — Day sheet</h1><div class="sub">${label} · ${dayLabel} · ${list.length} stop(s)</div>${bodyHtml}<button class="noprint" onclick="window.print()" style="margin-top:20px;padding:8px 14px">Print</button></body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Allow pop-ups to print the day sheet");
      return;
    }
    w.document.write(html);
    w.document.close();
    setTimeout(() => {
      try {
        w.print();
      } catch {
        /* user can print from the button */
      }
    }, 400);
  };

  return (
    <div data-testid="job-field-view">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex gap-2" data-testid="field-crew-filter">
          {[
            [false, "Whole shop"],
            [true, "My stops"],
          ].map(([value, label]) => (
            <button
              key={label}
              type="button"
              data-testid={`field-filter-${value ? "mine" : "all"}`}
              onClick={() => setMineOnly(value)}
              className={`h-10 rounded-lg border px-4 text-xs font-bold uppercase tracking-widest transition-colors duration-150 ${
                mineOnly === value
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="day-sheet-button"
              className="ml-auto inline-flex h-10 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-4 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <Printer size={14} weight="bold" /> Day sheet
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-zinc-800 bg-zinc-900">
            <DropdownMenuItem
              data-testid="day-sheet-today"
              onClick={() => printDaySheet(today, "Today")}
            >
              Today
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="day-sheet-tomorrow"
              onClick={() => printDaySheet(addDaysIso(1), "Tomorrow")}
            >
              Tomorrow
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {shown === 0 && (
        <div
          data-testid="field-empty"
          className="rounded-lg border border-dashed border-zinc-800 p-12 text-center"
        >
          <CheckCircle
            size={30}
            weight="bold"
            className="mx-auto text-zinc-700"
          />
          <p className="mt-3 text-sm text-zinc-400">
            {mineOnly
              ? "No stops assigned to you yet."
              : "No upcoming template, fabrication, install or service stops."}
          </p>
        </div>
      )}

      <div className="space-y-8">
        {groups.map(([label, list]) =>
          list.length === 0 ? null : (
            <div key={label} data-testid={`field-group-${label}`}>
              <div className="flex items-center gap-2 px-1 pb-3">
                <span
                  className={`text-xs font-semibold uppercase tracking-widest ${
                    label === "Overdue" ? "text-red-300" : "text-zinc-400"
                  }`}
                >
                  {label}
                </span>
                <span className="font-mono-data text-xs text-zinc-500">
                  {list.length}
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {list.map((s) => {
                  const status = s.stop_status?.status || "Pending";
                  return (
                    <div
                      key={`${s.job_id}-${s.kind}`}
                      data-testid={`field-stop-${s.job_id}-${s.kind}`}
                      className={`flex flex-col rounded-lg border p-4 transition-colors duration-150 ${
                        status === "Done"
                          ? "border-emerald-500/30 bg-zinc-900/60"
                          : "border-zinc-800 bg-zinc-900"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${KIND_STYLE[s.kind]}`}
                        >
                          {s.kind} · {[s.time ? fmtTime12(s.time) : "", s.window && s.window !== "All day" ? s.window : ""].filter(Boolean).join(" · ") || "All day"}
                        </span>
                        <Link
                          to={`/projects/${s.job_id}?tab=field`}
                          className="font-mono-data text-xs text-yellow-500 hover:underline"
                        >
                          {s.code}
                        </Link>
                        <span className="ml-auto truncate text-xs text-zinc-500">
                          {s.crew || "Unassigned"}
                        </span>
                      </div>

                      <div className="mt-2 text-base font-bold leading-tight">
                        {s.project_name}
                      </div>
                      <div className="text-xs text-zinc-400">
                        {s.customer_name || "—"}
                        {label !== "Today" ? ` · ${s.date}` : ""}
                      </div>

                      {s.address && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-zinc-300">
                          <MapPin
                            size={13}
                            weight="bold"
                            className="mt-0.5 shrink-0 text-zinc-500"
                          />
                          <span className="min-w-0">{s.address}</span>
                        </div>
                      )}
                      {s.sink_location && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
                          <Drop size={13} weight="bold" className="shrink-0" />
                          Sink: {s.sink_location}
                        </div>
                      )}

                      {(s.stop_status?.on_site_at ||
                        s.stop_status?.done_at) && (
                        <div
                          data-testid={`field-times-${s.job_id}-${s.kind}`}
                          className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]"
                        >
                          {s.stop_status?.on_site_at && (
                            <span className="inline-flex items-center gap-1.5 text-yellow-300/80">
                              <span className="h-1.5 w-1.5 rounded-full bg-yellow-400" />
                              Arrived {fmtClock(s.stop_status.on_site_at)}
                            </span>
                          )}
                          {s.stop_status?.done_at && (
                            <span className="inline-flex items-center gap-1.5 text-emerald-300/80">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                              Finished {fmtClock(s.stop_status.done_at)}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-3">
                        {s.address && (
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(s.address)}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`field-directions-${s.job_id}-${s.kind}`}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <NavigationArrow size={13} weight="bold" />
                            Directions
                          </a>
                        )}
                        {s.customer_phone && (
                          <a
                            href={`tel:${s.customer_phone}`}
                            data-testid={`field-call-${s.job_id}-${s.kind}`}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <Phone size={13} weight="bold" />
                            Call
                          </a>
                        )}
                        {s.customer_phone && (
                          <button
                            type="button"
                            data-testid={`field-onway-${s.job_id}-${s.kind}`}
                            onClick={() => openOnMyWay(s)}
                            disabled={texting === `${s.job_id}-${s.kind}`}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-indigo-500/50 bg-indigo-500/10 px-3 text-[11px] font-semibold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20 disabled:opacity-50"
                          >
                            <ChatCircle size={13} weight="bold" /> On my way
                          </button>
                        )}
                        {(s.kind === "Install" || s.kind === "Template") && (
                          <button
                            type="button"
                            data-testid={`field-label-${s.job_id}-${s.kind}`}
                            onClick={() => setLabelDlg({ jobId: s.job_id, kind: s.kind })}
                            title="Print the 4x6 field label"
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-cyan-500/50 hover:text-cyan-200"
                          >
                            <Printer size={13} weight="bold" /> Label
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`field-email-${s.job_id}-${s.kind}`}
                          onClick={() => setEmailJob(s.job_id)}
                          title="Email the customer"
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-violet-500/50 bg-violet-500/10 px-3 text-[11px] font-semibold uppercase tracking-widest text-violet-200 transition-colors duration-150 hover:bg-violet-500/20"
                        >
                          <EnvelopeSimple size={13} weight="bold" /> Email
                        </button>
                        <button
                          type="button"
                          data-testid={`field-cycle-${s.job_id}-${s.kind}`}
                          onClick={() => cycle(s)}
                          className={`ml-auto inline-flex h-9 items-center rounded-lg border px-4 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${STATUS_STYLE[status]}`}
                        >
                          {status}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ),
        )}
      </div>
      <ConfirmTextDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        to={confirm?.to}
        firstName={(confirm?.s?.customer_name || "").split(" ")[0]}
        message={confirm?.message || ""}
        title="On my way — review text"
        sending={!!texting}
        onSend={doSendOnMyWay}
      />
      <LabelDialog
        jobId={labelDlg?.jobId}
        open={!!labelDlg}
        onOpenChange={(o) => !o && setLabelDlg(null)}
        kind={labelDlg?.kind || "Install"}
      />
      <JobEmailDialog
        open={!!emailJob}
        onOpenChange={(o) => !o && setEmailJob(null)}
        jobId={emailJob}
      />
    </div>
  );
};
